import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { msgid, ngettext, t } from 'ttag';
import { LearnerPretestStatus, usePretestQuestionsQuery } from '~/data';
import { select } from '~/lib/select';
import { Flex } from '~/ui/flex/Flex';
import { Spinner } from '~/ui/spinner/Spinner';
import { Chapter, TrainingState, useTrainingStore } from '../state';
import { useTrainingParams } from '../useTrainingParams';
import { PretestExitDialog } from './PretestExitDialog';
import { PretestQuestionSlide } from './PretestQuestionSlide';
import {
  PretestButton,
  PretestScreen,
  SecondaryButton,
  TextButton
} from './PretestScreen';
import { questionOptions } from './pretestSlide';

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background-color: ${p => p.theme.colors.pinkLight};
`;

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: md;
  padding: sm md;
  background-color: ${p => p.theme.colors.white};
  color: ${p => p.theme.colors.black};
`;

const ModeTitle = styled.div`
  display: grid;
  min-width: 0;

  span:first-child {
    font-family: ralewayBold;
    font-size: 13px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--color-accent-fg);
  }

  span:last-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: sm md;
  background-color: ${p => p.theme.colors.white};
  border-top: 1px solid ${p => p.theme.colors.greyMed};
`;

const ProgressRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 16px;

  &:focus {
    outline: none;
  }
`;

const Track = styled.div`
  height: 6px;
  border-radius: 999px;
  background-color: var(--color-grey-lt);
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background-color: var(--color-accent-dk);
  }
`;

const Chip = styled.span`
  display: inline-block;
  max-width: 100%;
  padding: 2px 12px;
  border: 1px solid var(--color-grey-md);
  border-radius: 14px;
  background-color: var(--color-grey-lt);
  font-family: ralewayBold;
  font-size: 14px;
`;

const SlideArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  display: grid;
`;

const Alert = styled.div`
  margin: sm md 0;
  padding: sm md;
  border-radius: 12px;
  border: 1px solid var(--color-red-md);
  background-color: var(--color-red-lt);

  p {
    margin: 0;
  }
`;

const Note = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--color-grey-dk);
`;

const selector = (s: TrainingState) =>
  select(
    s,
    'title',
    'items',
    'pretestAnswers',
    'submitPretest',
    'declinePretest',
    'refreshPretest',
    'restorePretestAnswers',
    'pretestBusy'
  );

/**
 * The assessment: one drawn question at a time in the normal slide renderer, with a simple header (the
 * mode, the course, Exit), progress, and the chapter each question tests. Nothing is graded or marked
 * until the learner submits; the pretest page then shows the server's result.
 */
export const PretestQuestions = () => {
  const { learnerTrainingId } = useTrainingParams();
  const {
    title,
    items,
    pretestAnswers,
    submitPretest,
    declinePretest,
    refreshPretest,
    restorePretestAnswers,
    pretestBusy
  } = useTrainingStore(selector);
  const { data, loading, error, refetch } = usePretestQuestionsQuery({
    variables: { learnerTrainingUuid: learnerTrainingId! },
    // The slides are only needed here. Caching them would also replace the
    // pretest the course loaded for this learner training, so skip the cache.
    fetchPolicy: 'no-cache',
    // Loading them again after a failure shows as loading.
    notifyOnNetworkStatusChange: true
  });
  // The questions whose pretest the store already took from the server.
  const [refreshedData, setRefreshedData] = useState<typeof data>();
  const [index, setIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const progressRef = useRef<HTMLHeadingElement>(null);
  const exitButtonRef = useRef<HTMLButtonElement>(null);

  const questions = data?.learnerTraining.pretest?.questions ?? [];
  const question = questions[index];
  const chapters = items.filter(
    (item): item is Chapter => item.kind === 'chapter'
  );

  useEffect(() => {
    const pretest = data?.learnerTraining.pretest;
    if (pretest?.id) {
      restorePretestAnswers(
        pretest.id,
        pretest.questions.map(question => ({
          id: question.id,
          optionIds: questionOptions(question).optionIds
        }))
      );
      // A learner who comes back continues at the first question they haven't
      // answered, or at the last one, ready to submit, when all are answered.
      const restored = useTrainingStore.getState().pretestAnswers;
      const unanswered = pretest.questions.findIndex(
        question =>
          questionOptions(question).optionIds.length > 0 &&
          (restored[question.id] ?? []).length === 0
      );
      setIndex(
        unanswered === -1
          ? Math.max(pretest.questions.length - 1, 0)
          : unanswered
      );
    }
  }, [data, restorePretestAnswers]);

  // Another tab may have submitted or declined the pretest meanwhile. Once the
  // store has it as the server does, the pretest page shows its screen instead.
  const movedOn =
    !!data &&
    data.learnerTraining.pretest?.status !== LearnerPretestStatus.InProgress;

  useEffect(() => {
    if (!movedOn) return;
    let current = true;
    void refreshPretest().then(() => {
      if (current) setRefreshedData(data);
    });
    return () => {
      current = false;
    };
  }, [data, movedOn, refreshPretest]);

  useEffect(() => {
    // Screen readers start each question at its number, as with a new slide.
    progressRef.current?.focus();
  }, [question?.id]);

  // Declining hands the learner to the course: the pretest page follows the pretest status once the server
  // accepts it. Until the server answers any pretest request, every choice here waits (pretestBusy).
  const decline = () => {
    void declinePretest();
  };

  // A graded submit hands over to the results; one that did not go through keeps the answers to send again.
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    setFailed(!(await submitPretest(questions.map(q => q.id))));
    setSubmitting(false);
  };

  // A load that failed for a moment is tried again, keeping the chance to test out.
  const retry = () => {
    refetch().catch(() => {
      // The error link already told the learner; this screen stays to try again.
    });
  };

  if (loading || (movedOn && refreshedData !== data)) return <Spinner />;

  if (error || movedOn || !question) {
    return (
      <PretestScreen>
        <x.p m={0}>{t`The knowledge assessment could not be loaded.`}</x.p>
        <Flex flexDirection="column" gap="sm">
          <PretestButton w="100%" onClick={retry} disabled={pretestBusy}>
            {t`Try again`}
          </PretestButton>
          <SecondaryButton w="100%" onClick={decline} disabled={pretestBusy}>
            {t`Skip assessment and proceed to course`}
          </SecondaryButton>
        </Flex>
      </PretestScreen>
    );
  }

  const isAnswered = (q: typeof question) =>
    questionOptions(q).optionIds.length === 0 ||
    (pretestAnswers[q.id] ?? []).length > 0;
  // A question that offers nothing to choose cannot hold the learner back.
  const answered = isAnswered(question);
  const answeredCount = questions.filter(
    q => (pretestAnswers[q.id] ?? []).length > 0
  ).length;
  const isLast = index === questions.length - 1;
  const chapterIndex = chapters.findIndex(
    chapter => chapter.id === question.chapterId
  );

  let submitButton = (
    <PretestButton
      onClick={submit}
      disabled={!answered || pretestBusy}
      data-test-id="submit-pretest-button"
    >
      {failed ? t`Try again` : t`Submit assessment`}
    </PretestButton>
  );
  if (submitting) {
    submitButton = (
      <PretestButton disabled data-test-id="submit-pretest-button">
        {t`Submitting…`}
      </PretestButton>
    );
  }

  return (
    <Layout>
      <Bar>
        <ModeTitle>
          <span>{t`Knowledge assessment`}</span>
          <span>{title}</span>
        </ModeTitle>
        <TextButton
          ref={exitButtonRef}
          onClick={() => setExitOpen(true)}
          disabled={pretestBusy}
          aria-haspopup="dialog"
        >
          {t`Exit`}
        </TextButton>
      </Bar>
      <Progress>
        <ProgressRow>
          <Heading ref={progressRef} tabIndex={-1}>
            {t`Question ${index + 1} of ${questions.length}`}
          </Heading>
          <span>
            {ngettext(
              msgid`${answeredCount} answered`,
              `${answeredCount} answered`,
              answeredCount
            )}
          </span>
        </ProgressRow>
        <Track
          role="progressbar"
          aria-label={t`Assessment progress`}
          aria-valuemin={1}
          aria-valuemax={questions.length}
          aria-valuenow={index + 1}
        >
          <span
            style={{ width: `${((index + 1) / questions.length) * 100}%` }}
          />
        </Track>
        {chapterIndex >= 0 && (
          <Chip>{t`Chapter ${chapterIndex + 1} · ${chapters[chapterIndex].title}`}</Chip>
        )}
      </Progress>
      {failed && (
        <Alert role="alert">
          <p>
            {t`We couldn’t submit your answers. They’re still here. Check your connection and try again.`}
          </p>
        </Alert>
      )}
      <SlideArea aria-busy={submitting}>
        <PretestQuestionSlide key={question.id} question={question} />
      </SlideArea>
      <Bar>
        {index > 0 ? (
          <TextButton
            onClick={() => setIndex(index - 1)}
            disabled={pretestBusy}
          >
            {t`Previous`}
          </TextButton>
        ) : (
          <span />
        )}
        {isLast ? (
          <x.div
            display="flex"
            flexDirection="column"
            alignItems="flex-end"
            gap="6px"
          >
            <Note>{t`Submitting ends the assessment. You’ll see your results right after.`}</Note>
            {submitButton}
          </x.div>
        ) : (
          <PretestButton
            onClick={() => setIndex(index + 1)}
            disabled={!answered || pretestBusy}
            data-test-id="next-pretest-question-button"
          >
            {t`Next`}
          </PretestButton>
        )}
      </Bar>
      <x.div
        role="status"
        aria-live="polite"
        position="absolute"
        w="1px"
        h="1px"
        overflow="hidden"
      >
        {submitting ? t`Submitting your answers.` : ''}
      </x.div>
      <PretestExitDialog
        open={exitOpen}
        onStay={() => setExitOpen(false)}
        onSkip={decline}
        busy={pretestBusy}
        returnFocusRef={exitButtonRef}
      />
    </Layout>
  );
};
